import { DOCUMENT } from '@angular/common';
import { Injectable, Renderer2, RendererFactory2, inject, signal } from '@angular/core';
import { LOCAL_STORAGE_KEYS } from '../../enums/local-storage.enum';
import { Theme } from '../../enums/theme.enum';
import { StorageService } from '../storage/storage-service';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private readonly document = inject(DOCUMENT);
    private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
    private readonly storageService = inject(StorageService);

    private readonly currentTheme = signal<Theme>(Theme.LIGHT);
    readonly theme = this.currentTheme.asReadonly();
    readonly isDark = signal(false);

    async initialize() {
        const stored = this.storageService.getLocalItem<Theme>(LOCAL_STORAGE_KEYS.THEME);
        this.currentTheme.set(stored ?? Theme.LIGHT);
        this.isDark.set(this.currentTheme() === Theme.DARK);
        this.applyTheme();
    }

    async setTheme(theme: Theme): Promise<void> {
        this.currentTheme.set(theme);
        this.isDark.set(theme === Theme.DARK);
        this.storageService.setLocalItem<Theme>(LOCAL_STORAGE_KEYS.THEME, theme);
        this.applyTheme();
    }

    async toggleTheme(): Promise<void> {
        await this.setTheme(this.isDark() ? Theme.LIGHT : Theme.DARK);
    }

    private applyTheme(): void {
        this.renderer.setAttribute(this.document.documentElement, 'data-theme', this.currentTheme());
    }
}
